/**
 * McInput Web Component
 * ---------------------
 * Questo file definisce il componente custom <mc-input> per la gestione avanzata degli input HTML.
 * Fornisce validazione lato client, gestione degli errori con messaggi localizzati, e supporto per autosubmit.
 * Utilizza le traduzioni definite in window.theme.t.errors.
 * 
 * Funzionalità principali:
 * - Validazione automatica su keyup, blur e change.
 * - Messaggi di errore localizzati e personalizzabili.
 * - Supporto per campi che devono essere uguali ad altri (es. conferma password).
 * - Autosubmit opzionale del form.
 */

// Import dinamico dei moduli di supporto (element, utils, shopify, events)
await themeComponent(theme.components['mc-element'],false);
await themeResource(theme.resources['mc-utils'], false);
await themeResource(theme.resources['mc-shopify'], false);
//await themeResource(theme.resources['mc-events'], false);

// Definizione della classe custom element McInput
class McInput extends McElement {
  input;      // Riferimento all'elemento input
  error;      // Container per il messaggio di errore
  errorText;  // Testo del messaggio di errore
  isValid;    // Stato di validità dell'input
  static ERROR_CLASS = 'is-invalid'; // Classe CSS per indicare un errore

  // Metodo di inizializzazione: collega input, error e listener
  init = () => {
    this.error = this.querySelector('[data-error]'); // Seleziona il container per l'errore
    this.input = this.input ? this.input : this.querySelector('input'); // Seleziona l'elemento input

    // Controlli di presenza elementi e traduzioni
    if (!this.input) { console.error('McInput: No input element found'); return; }
    if(!this.error){ console.error('McInput: No error container found'); return; }
    if(!window.theme?.t) { console.error('McInput: Translations not available'); return; }
    if(!window.theme.t.errors) { console.error('McInput: Translations for errors not available'); return; }

    // Listener per validazione su keyup, blur e change
    //this.input.addEventListener('keyup', this.validate.bind(this));
    this.input.addEventListener('blur', this.validate.bind(this));
    this.input.addEventListener('change', this.validate.bind(this));
    this.autosubmit(); // Configura autosubmit se richiesto
  }

  // Valida l'input utilizzando la proprietà `validity` e aggiorna errorText
  validate = (e) => {
    this.isValid = this.input.checkValidity(); // Controlla la validità dell'input
    if (!this.isValid) {
      // Gestione dei vari tipi di errori di validazione HTML5
      if (this.input.validity.badMcInput) {
        this.validateType();
      } else if (this.input.validity.valueMissing) {
        this.errorText = theme.t.errors.required;
      } else if (this.input.validity.typeMismatch) {
        this.validateType();
      } else if (this.input.validity.tooShort) {
        this.errorText = theme.t.errors.minLength.replace('{{ min }}', this.input.minLength);
      } else if (this.input.validity.tooLong) {
        this.errorText = theme.t.errors.maxLength.replace('{{ max }}', this.input.maxLength);
      } else if (this.input.validity.rangeUnderflow) {
        this.errorText = theme.t.errors.min.replace('{{ min }}', this.input.min);
      } else if (this.input.validity.rangeOverflow) {
        this.errorText = theme.t.errors.max.replace('{{ max }}', this.input.max);
      } else if (this.input.validity.stepMismatch) {
        this.errorText = theme.t.errors.step;
      } else if (this.input.validity.patternMismatch) {
        this.errorText = this.dataset.patternMsg || theme.t.errors.pattern;
      } else if (this.input.validity.customError) {
        this.errorText = this.input.validationMessage;
      }
    }

    // Validazione "equals": confronta con altro campo se richiesto
    if (this.isValid && this.dataset.equals != undefined) {
      const equalsInput = document.getElementById(this.dataset.equals);
      if (!equalsInput) {
        console.warn(`McInput: Element with ID ${this.dataset.equals} not found for equals validation`);
        return;
      }
      if (this.input.value != equalsInput.value) {
        this.isValid = false;
        this.errorText = theme.t.errors.equals;
      }
    }

    // Emette eventi custom in base alla validità
    if (this.isValid) {
      McShopify.customEvent(MCEVENTS.INPUT_VALID, {value: this.input.value}, this);
    } else {
      McShopify.customEvent(MCEVENTS.INPUT_ERROR, {message: this.errorText}, this);
    }
    this.showError(); // Mostra o nasconde il messaggio di errore
  }

  // Imposta errorText in base al tipo di input (email, url, ecc.)
  validateType = () => {
    switch (this.input.type) {
      case 'email':
        this.errorText = theme.t.errors.email;
        break;
      case 'url':
        this.errorText = theme.t.errors.url;
        break;
      case 'number':
        this.errorText = theme.t.errors.number;
        break;
      case 'integer':
        this.errorText = theme.t.errors.integer;
        break;
      case 'date':
        this.errorText = theme.t.errors.date;
        break;
      case 'time':
        this.errorText = theme.t.errors.time;
        break;
      case 'datetime':
        this.errorText = theme.t.errors.datetime;
        break;
      default:
        this.errorText = theme.t.errors.pattern;
        break;
    }
  }

  // Mostra o nasconde il messaggio di errore nell'elemento error
  showError = () => {
    this.isValid ? this.error.innerText = '' : this.error.innerText = this.errorText;
    this.isValid ? this.classList.remove(McInput.ERROR_CLASS) : this.classList.add(McInput.ERROR_CLASS);
  }

  // Se abilitato, invia automaticamente il form al change
  autosubmit = () => {
    if (this.dataset.autosubmit != undefined) {
      this.input.addEventListener('change', () => {
        const form = this.input.form.closest('mc-form');
        form?.submit();
      });
    }
  }
}

// Esporta la classe McInput nel contesto globale e come modulo
window.McInput = McInput;
export default McInput;